/* eslint-disable react/no-unescaped-entities */
/*
 * EnrollSuccess — the single most fragile UI moment in the whole app.
 *
 * Stripe redirects here on ANY successful checkout, which means the moment the
 * customer lands, their card has already been charged. Regardless of what our
 * backend polling reports, the affirmative "payment received" state is safe to
 * lead with. Everything else on the page — magic-link timing, account
 * provisioning progress, resend flow, support fallbacks — orbits around
 * reassurance: their money is safe with Stripe, we know they exist, and
 * we're going to walk them through the next 60 seconds without abandonment.
 *
 * This is the page most likely to be viewed after Bug B recurrences (Stripe
 * wrapper flake, magic link delayed, etc.) — every state gets a support
 * contact + clear next action so the customer never dead-ends here.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { paymentsApi } from "./portal/shell";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import SEO from "../components/SEO";
import { toast, Toaster } from "sonner";
import {
  CheckCircle2, Loader2, ShieldAlert, Mail, Clock, RefreshCw,
  LifeBuoy, ArrowRight, FileText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 60_000; // Keep trying for a full minute — Stripe webhooks can lag on cold accounts

export default function EnrollSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);   // last /status response
  const [phase, setPhase] = useState(sessionId ? "checking" : "missing");
  const [errorMsg, setErrorMsg] = useState("");
  const startedAt = useRef(Date.now());
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const r = await paymentsApi.status(sessionId);
        if (stopped) return;
        setStatus(r);
        if (r.payment_status === "paid" && r.account_ready) {
          setPhase("ready");
          return;
        }
        if (r.payment_status === "paid" && !r.account_ready) {
          setPhase("paid-provisioning");
        } else if (r.status === "expired") {
          setPhase("expired");
          return;
        }
        if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
          // Card charged but we can't confirm — customer needs a human path.
          setPhase(r.payment_status === "paid" ? "paid-slow" : "unknown");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (ex) {
        if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
          setPhase("error");
          setErrorMsg(ex.message || "Unable to confirm payment status");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-tb-bg text-tb-text flex flex-col">
      <SEO
        title="Enrollment Confirmed — Thunderbird Geospatial"
        description="Your Advanced RPAS Ground School seat is confirmed."
        canonical="/enroll/success"
      />
      <Toaster theme="dark" position="top-right" />
      <Navbar />
      <main className="flex-1 pt-32 pb-20 px-6" data-testid="enroll-success-page">
        <div className="max-w-3xl mx-auto">
          {phase === "missing" ? (
            <MissingSessionCard />
          ) : (
            <>
              {/* Always affirm payment success first — Stripe only redirects here on success. */}
              <PaymentReceivedBanner status={status} />
              <div className="mt-8 grid gap-6">
                {phase === "checking" && <ProvisioningCard label="Confirming your payment with Stripe" />}
                {phase === "paid-provisioning" && <ProvisioningCard label="Setting up your account" note="Any moment now — we're issuing your magic link." />}
                {phase === "ready" && <ReadyCard sessionId={sessionId} />}
                {phase === "paid-slow" && <SlowProvisioningCard sessionId={sessionId} />}
                {phase === "expired" && <ExpiredCard />}
                {phase === "unknown" && <UnknownCard sessionId={sessionId} errorMsg={errorMsg} />}
                {phase === "error" && <ErrorCard sessionId={sessionId} errorMsg={errorMsg} />}
              </div>
              <SupportFooter sessionId={sessionId} />
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* ---------------------------- Building blocks ---------------------------- */

function PaymentReceivedBanner({ status }) {
  const amount = status?.amount_total ? (status.amount_total / 100).toFixed(2) : null;
  const currency = (status?.currency || "cad").toUpperCase();
  return (
    <div className="rounded-2xl border border-tb-teal/40 bg-gradient-to-br from-tb-teal/[0.08] to-transparent p-8" data-testid="enroll-payment-received">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-full bg-tb-teal/[0.14] border border-tb-teal/60 grid place-items-center flex-shrink-0">
          <CheckCircle2 size={22} className="text-tb-teal" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-[0.28em] text-tb-teal uppercase">Payment received</div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-white mt-2">
            Welcome to the Advanced RPAS Ground School.
          </h1>
          <p className="mt-3 text-sm sm:text-base text-tb-text/85 leading-relaxed">
            Your seat is confirmed. Your card was charged{amount ? <> <span className="text-white">CA${amount} {currency}</span></> : ""} and Stripe has the receipt.
            The rest of this page walks you through the next 60 seconds.
          </p>
        </div>
      </div>
    </div>
  );
}

function ProvisioningCard({ label, note }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex items-center gap-4" data-testid="enroll-provisioning">
      <Loader2 size={18} className="text-tb-teal animate-spin flex-shrink-0" />
      <div>
        <div className="text-sm text-white">{label}…</div>
        {note && <div className="text-xs text-tb-text/60 mt-1">{note}</div>}
      </div>
    </div>
  );
}

function ReadyCard({ sessionId }) {
  return (
    <div className="rounded-xl border border-tb-teal/40 bg-tb-teal/[0.04] p-6" data-testid="enroll-success-paid">
      <div className="flex items-center gap-3">
        <Mail size={20} className="text-tb-teal" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-tb-teal uppercase">
          Magic link sent — check your inbox
        </div>
      </div>
      <div className="mt-4 space-y-4">
        <NextStep n={1} title="Look for the email">
          <div className="mt-1 rounded-lg border border-white/10 bg-black/40 p-3 text-[13px]">
            <div className="font-mono text-[10px] tracking-[0.15em] text-tb-text/45 uppercase">From</div>
            <div className="text-white">Thunderbird Geospatial &lt;onboarding@resend.dev&gt;</div>
            <div className="font-mono text-[10px] tracking-[0.15em] text-tb-text/45 uppercase mt-2">Subject</div>
            <div className="text-white">Set your password — Advanced RPAS Ground School</div>
          </div>
        </NextStep>
        <NextStep n={2} title="Click the magic link">
          <p className="text-sm text-tb-text/80 mt-1 leading-relaxed">
            It's valid for 48 hours. Set your password and you'll be dropped straight into the course dashboard.
          </p>
        </NextStep>
        <NextStep n={3} title="Start with Module 1: Air Law">
          <p className="text-sm text-tb-text/80 mt-1 leading-relaxed">
            Every module has lessons, a self-check quiz, and links to the Transport Canada references. Take your time — the practice exam is untimed and unlimited retries.
          </p>
        </NextStep>
      </div>

      <ResendMagicLink />

      <div className="mt-6 flex gap-3 flex-wrap">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-tb-teal text-black font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-white transition"
          data-testid="enroll-login-link"
        >
          Already set your password? Log in <ArrowRight size={13} />
        </Link>
        <a
          href={`${API}/payments/receipts/${sessionId}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/20 text-white/85 font-mono text-[11px] tracking-[0.15em] uppercase hover:border-tb-teal hover:text-tb-teal transition"
          data-testid="enroll-download-receipt"
        >
          <FileText size={13} /> Download tax invoice (PDF)
        </a>
      </div>
      {sessionId && <SessionReceipt sessionId={sessionId} />}
    </div>
  );
}

function SlowProvisioningCard({ sessionId }) {
  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-500/[0.05] p-6" data-testid="enroll-slow-provisioning">
      <div className="flex items-center gap-3">
        <Clock size={20} className="text-amber-400" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-amber-400 uppercase">
          Your payment is safe — provisioning is a bit slow
        </div>
      </div>
      <p className="mt-4 text-sm text-tb-text/85 leading-relaxed">
        Stripe has confirmed your payment. Our account setup is taking a little longer than usual to finish issuing your magic link (this happens occasionally — nothing wrong with your payment).
      </p>
      <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-4 text-[13px] leading-relaxed">
        <strong className="text-white">Two things you can do right now:</strong>
        <ol className="mt-2 space-y-2 list-decimal list-inside text-tb-text/80">
          <li>Check your inbox in <strong className="text-white">2–3 minutes</strong>. The magic link should arrive from <span className="font-mono text-white">onboarding@resend.dev</span> with subject <em>"Set your password"</em>.</li>
          <li>If it doesn't arrive in <strong className="text-white">5 minutes</strong>, use the resend option below (or email us — we'll set up your account manually).</li>
        </ol>
      </div>
      <ResendMagicLink />
      {sessionId && <SessionReceipt sessionId={sessionId} />}
    </div>
  );
}

function ExpiredCard() {
  return (
    <div className="rounded-xl border border-red-400/40 bg-red-500/[0.06] p-6" data-testid="enroll-expired">
      <div className="flex items-center gap-3">
        <ShieldAlert size={20} className="text-red-400" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-red-400 uppercase">
          Checkout session expired
        </div>
      </div>
      <p className="mt-4 text-sm text-tb-text/85 leading-relaxed">
        Your Stripe checkout session expired before payment completed. Good news: no charge was made. Just start enrollment again and finish within 24 hours of clicking the button.
      </p>
      <Link
        to="/groundschool#pricing"
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-tb-teal/60 text-tb-teal font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-tb-teal hover:text-black transition"
      >
        Try enrolling again <ArrowRight size={13} />
      </Link>
    </div>
  );
}

function UnknownCard({ sessionId, errorMsg }) {
  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-500/[0.05] p-6" data-testid="enroll-unknown">
      <div className="flex items-center gap-3">
        <ShieldAlert size={20} className="text-amber-400" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-amber-400 uppercase">
          Can't confirm status right now
        </div>
      </div>
      <p className="mt-4 text-sm text-tb-text/85 leading-relaxed">
        We haven't been able to confirm your payment status in the last minute. <strong className="text-white">Your money is safe</strong> — either the charge went through (and the email is on the way) or it didn't (and there's nothing to refund).
      </p>
      <p className="mt-3 text-sm text-tb-text/85 leading-relaxed">
        Please contact us directly and quote the session reference below — we can look it up in Stripe in under a minute and get you sorted manually if anything did go wrong.
      </p>
      {errorMsg && (
        <div className="mt-3 font-mono text-[11px] text-tb-text/45">
          Technical detail: {errorMsg}
        </div>
      )}
      {sessionId && <SessionReceipt sessionId={sessionId} />}
    </div>
  );
}

function ErrorCard({ sessionId, errorMsg }) {
  return (
    <div className="rounded-xl border border-red-400/40 bg-red-500/[0.06] p-6" data-testid="enroll-error">
      <div className="flex items-center gap-3">
        <ShieldAlert size={20} className="text-red-400" />
        <div className="font-mono text-[10px] tracking-[0.24em] text-red-400 uppercase">
          Payment status unavailable
        </div>
      </div>
      <p className="mt-4 text-sm text-tb-text/85 leading-relaxed">
        We couldn't reach our payment provider to confirm the details of this session. <strong className="text-white">Your card was only charged if Stripe returned you here successfully.</strong>
      </p>
      <p className="mt-3 text-sm text-tb-text/85 leading-relaxed">
        Reach out to us with the session reference below and we'll verify manually. If a charge did go through, you'll have an account by end of day.
      </p>
      {errorMsg && (
        <div className="mt-3 font-mono text-[11px] text-tb-text/45">
          Technical detail: {errorMsg}
        </div>
      )}
      {sessionId && <SessionReceipt sessionId={sessionId} />}
    </div>
  );
}

function MissingSessionCard() {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8" data-testid="enroll-missing-session">
      <div className="font-mono text-[10px] tracking-[0.24em] text-tb-text/45 uppercase">No session provided</div>
      <h1 className="text-2xl font-display font-bold tracking-tight text-white mt-3">This page confirms a checkout.</h1>
      <p className="mt-3 text-sm text-tb-text/75 leading-relaxed">
        Looks like you landed here without a payment session in the URL. If you meant to enroll, head to the course page. If you just paid and something looks off, contact us with your Stripe receipt.
      </p>
      <div className="mt-4 flex gap-3 flex-wrap">
        <Link
          to="/groundschool"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-tb-teal/60 text-tb-teal font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-tb-teal hover:text-black transition"
        >
          Course page <ArrowRight size={13} />
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/20 text-tb-text font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-white/10 transition"
        >
          Log in
        </Link>
      </div>
    </div>
  );
}

function NextStep({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-6 h-6 rounded-full border border-tb-teal/60 grid place-items-center font-mono text-[11px] text-tb-teal flex-shrink-0 mt-0.5">{n}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-white">{title}</div>
        {children}
      </div>
    </div>
  );
}

function ResendMagicLink() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (busy || !email.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/students/request-magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      // Response is intentionally opaque server-side (200 whether email exists or not)
      if (!r.ok) throw new Error("Please try again in a moment");
      setSent(true);
      toast.success("If that email is enrolled, a fresh magic link is on the way.");
    } catch (ex) {
      toast.error(ex.message || "Could not send resend request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-white/10 bg-black/30 p-4">
      <div className="font-mono text-[10px] tracking-[0.2em] text-tb-text/55 uppercase flex items-center gap-2">
        <RefreshCw size={11} /> Didn't get the email in 5 minutes?
      </div>
      {sent ? (
        <p className="mt-3 text-sm text-tb-teal">
          Requested. Check your inbox again — it usually arrives within a minute.
        </p>
      ) : (
        <div className="mt-3 flex gap-2 flex-wrap">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            data-testid="enroll-resend-email"
            className="flex-1 min-w-[220px] rounded-md bg-black/40 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-tb-text/40 focus:border-tb-teal focus:outline-none"
          />
          <button
            onClick={send}
            disabled={busy || !email.trim()}
            data-testid="enroll-resend-submit"
            className="px-4 py-2 rounded-md bg-tb-teal/[0.14] border border-tb-teal/60 text-tb-teal font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-tb-teal hover:text-black transition disabled:opacity-50"
          >
            {busy ? "Sending…" : "Resend link"}
          </button>
        </div>
      )}
    </div>
  );
}

function SessionReceipt({ sessionId }) {
  const [copied, setCopied] = useState(false);
  const shortId = sessionId.length > 24 ? `${sessionId.slice(0, 8)}…${sessionId.slice(-6)}` : sessionId;
  const copy = () => {
    navigator.clipboard?.writeText(sessionId).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => {},
    );
  };
  return (
    <div className="mt-5 pt-5 border-t border-white/5 flex items-center gap-3 flex-wrap" data-testid="enroll-session-receipt">
      <div className="font-mono text-[10px] tracking-[0.18em] text-tb-text/45 uppercase">Session reference</div>
      <button
        onClick={copy}
        className="font-mono text-[11px] text-tb-text/70 hover:text-tb-teal transition"
        title="Click to copy the full session id — include this if you contact support"
      >
        {copied ? "Copied ✓" : shortId}
      </button>
    </div>
  );
}

function SupportFooter({ sessionId }) {
  return (
    <div className="mt-10 pt-8 border-t border-white/5 flex items-start gap-3" data-testid="enroll-support-footer">
      <LifeBuoy size={16} className="text-tb-text/55 mt-0.5 flex-shrink-0" />
      <div className="text-sm text-tb-text/70 leading-relaxed">
        <strong className="text-white">Stuck?</strong>{" "}
        Email us at{" "}
        <a href="mailto:admin@tbirdgeo.com" className="text-tb-teal hover:text-white transition">admin@tbirdgeo.com</a>{" "}
        {sessionId ? "and mention your session reference above — we'll sort it manually." : "and we'll help you sort it out."}
      </div>
    </div>
  );
}
